import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useEffect, useState, useCallback } from 'react';
import { useSignOut } from 'react-firebase-hooks/auth';
import { auth } from '../../config/firebase';
import { FiLogOut, FiBell } from 'react-icons/fi';
import '../../styles/Admin-global.css';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signOut] = useSignOut(auth);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    expiring: 0
  });
  
  // Notification states
  const [allIncidents, setAllIncidents] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationLoading, setNotificationLoading] = useState(false);
  

  const today = new Date().toISOString().split("T")[0];

  // Check if device is overdue
  const isDeviceOverdue = (lastSubmissionDate, checkFrequency) => {
    if (!lastSubmissionDate || !checkFrequency) return false;
    
    const lastDate = new Date(lastSubmissionDate);
    const todayDate = new Date(today);
    const daysDifference = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));
    
    return daysDifference > checkFrequency;
  };

  // Fetch all incidents across all tenants
  const fetchAllIncidents = useCallback(async () => {
    try {
      setNotificationLoading(true);
      const incidents = [];

      // Get all tenants
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      
      for (const tenantDoc of tenantsSnapshot.docs) {
        const tenantId = tenantDoc.id;
        const tenantName = tenantDoc.data().name || tenantId;

        // Get all locations for this tenant
        const locationsQuery = collection(db, `tenants/${tenantId}/locations`);
        const locationsSnapshot = await getDocs(locationsQuery);

        const locationsData = {};
        locationsSnapshot.docs.forEach(locDoc => {
          locationsData[locDoc.id] = locDoc.data().name || locDoc.id;
        });

        // Process each location's rooms
        for (const locationDoc of locationsSnapshot.docs) {
          const locationId = locationDoc.id;
          const locationName = locationsData[locationId];

          try {
            const roomsQuery = collection(db, `tenants/${tenantId}/locations/${locationId}/rooms`);
            const roomsSnapshot = await getDocs(roomsQuery);

            for (const roomDoc of roomsSnapshot.docs) {
              const roomId = roomDoc.id;
              const roomData = roomDoc.data();

              // Check room infrastructure status
              if (roomData.infraStatus && 
                  ['NOT-READY', 'WARNING', 'CRITICAL'].includes(roomData.infraStatus)) {
                incidents.push({
                  id: `room-${tenantId}-${locationId}-${roomId}`,
                  type: 'room',
                  tenantName,
                  locationName,
                  roomName: roomData.name || roomId,
                  infraStatus: roomData.infraStatus,
                  infraComment: roomData.infraComment || "No comments",
                  lastUpdated: roomData.lastUpdated?.toDate(),
                  severity: roomData.infraStatus === 'CRITICAL' ? 'high' : 
                           roomData.infraStatus === 'NOT-READY' ? 'high' : 'medium',
                  timestamp: roomData.lastUpdated?.toDate() || new Date()
                });
              }

              // Get latest submissions to check device status
              try {
                const submissionsQuery = query(
                  collection(db, `tenants/${tenantId}/locations/${locationId}/rooms/${roomId}/submissions`),
                  orderBy("timestamp", "desc")
                );

                const submissionsSnapshot = await getDocs(submissionsQuery);

                if (!submissionsSnapshot.empty) {
                  const latestSubmission = submissionsSnapshot.docs[0].data();
                  const submissionDate = latestSubmission.date;

                  if (latestSubmission.devices?.length) {
                    latestSubmission.devices.forEach((device) => {
                      // Check if device is not working
                      if (device.status === 'NOT-WORKING') {
                        incidents.push({
                          id: `device-${tenantId}-${locationId}-${roomId}-${device.id}`,
                          type: 'device',
                          tenantName,
                          locationName,
                          roomName: roomData.name || roomId,
                          deviceName: device.name || device.id,
                          status: device.status,
                          comment: device.comment || "No comments",
                          timestamp: latestSubmission.timestamp?.toDate() || new Date(),
                          severity: 'high'
                        });
                      }

                      // Check if device is overdue
                      const isOverdue = isDeviceOverdue(submissionDate, roomData.checkFrequency || 1);
                      if (isOverdue && device.status !== 'NOT-WORKING') {
                        const daysSinceLastCheck = Math.floor((new Date(today) - new Date(submissionDate)) / (1000 * 60 * 60 * 24));
                        incidents.push({
                          id: `overdue-${tenantId}-${locationId}-${roomId}-${device.id}`,
                          type: 'device',
                          tenantName,
                          locationName,
                          roomName: roomData.name || roomId,
                          deviceName: device.name || device.id,
                          status: device.status,
                          comment: `Device overdue for check (${daysSinceLastCheck} days)`,
                          timestamp: latestSubmission.timestamp?.toDate() || new Date(),
                          severity: 'medium',
                          isOverdue: true,
                          daysSinceLastCheck
                        });
                      }
                    });
                  }
                }
              } catch (submissionError) {
                console.error(`Error fetching submissions for room ${roomId}:`, submissionError);
              }
            }
          } catch (roomError) {
            console.error(`Error processing location ${locationId}:`, roomError);
          }
        }
      }

      setAllIncidents(incidents);
      setUnreadCount(incidents.length);
    } catch (error) {
      console.error("Error fetching all incidents:", error);
    } finally {
      setNotificationLoading(false);
    }
  }, [today]);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'tenants'));
        const tenantsData = [];
        let activeCount = 0;
        let inactiveCount = 0;
        let expiringCount = 0;
  
        const today = new Date();
  
        querySnapshot.forEach((doc) => {
          const tenantData = doc.data();
          tenantsData.push({
            id: doc.id,
            ...tenantData,
          });
  
          if (tenantData.status === 'inactive') {
            inactiveCount++;
          } else if (tenantData.status === 'active') {
            const expiryDate = tenantData.expiryDate?.toDate();
            if (expiryDate) {
              const diffInDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
              if (diffInDays <= 10 && diffInDays > 0) {
                expiringCount++;
              } else {
                activeCount++;
              }
            } else {
              activeCount++;
            }
          }
        });
  
        setTenants(tenantsData);
        setStats({
          total: tenantsData.length,
          active: activeCount,
          inactive: inactiveCount,
          expiring: expiringCount
        });
      } catch (error) {
        console.error('Error fetching tenants:', error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchTenants();
    fetchAllIncidents();
  }, [fetchAllIncidents]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('.admin-notifications-container')) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotifications]);

  const handleViewAllIncidents = () => {
    navigate('/admin/incidents');
  };

  const handleCreateUser = () => {
    navigate('/admin/users');
  };

  const handleManageTenants = () => {
    navigate('/admin/tenants');
  };

const handleTrackingReport = () => {
  navigate('/admin/trackingreports'); // Make sure this matches your route
};

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading tenant data...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-dashboard">
        <div className="admin-header">
          <img src="./teampresence-logo.png" alt="Admin Logo" className="admin-logo" />
          <h1 className="admin-title">Admin Dashboard</h1>
          <div className="admin-header-actions">
            <div className="admin-notifications-container">
              <button 
                className="admin-notification-bell"
                onClick={() => setShowNotifications(!showNotifications)}
                disabled={notificationLoading}
              >
                <FiBell size={20} />
                {unreadCount > 0 && (
                  <span className="admin-notification-badge">{unreadCount}</span>
                )}
              </button>
              
              {showNotifications && (
                <div className="admin-notifications-dropdown">
                  <div className="admin-notifications-header">
                    <h3>All Tenant Incidents</h3>
                    <button onClick={() => setShowNotifications(false)}>
                      ×
                    </button>
                  </div>
                  <div className="admin-notifications-list">
                    {notificationLoading ? (
                      <p>Loading incidents...</p>
                    ) : allIncidents.length === 0 ? (
                      <p>No incidents found</p>
                    ) : (
                      allIncidents.slice(0, 10).map(incident => (
                        <div 
                          key={incident.id}
                          className={`admin-notification-item ${incident.severity}`}
                        >
                          <div className="admin-notification-content">
                            <h4>{incident.type === 'room' ? 'Room Infrastructure Issue' : 'Device Issue'}</h4>
                            <p><strong>{incident.tenantName}</strong> - {incident.locationName}</p>
                            <p>
                              {incident.type === 'room' 
                                ? `${incident.roomName} - ${incident.infraStatus}`
                                : `${incident.deviceName} in ${incident.roomName} - ${incident.status?.replace('-', ' ')}`
                              }
                            </p>
                            <small>{incident.timestamp?.toLocaleString()}</small>
                          </div>
                        </div>
                      ))
                    )}
                    {allIncidents.length > 10 && (
                      <button 
                        className="view-all-incidents-btn"
                        onClick={handleViewAllIncidents}
                      >
                        View All {allIncidents.length} Incidents
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button className="admin-sign-out-btn" onClick={handleSignOut}>
              <FiLogOut className="sign-out-icon" />
            </button>
          </div>
        </div>
        
        <div className="dashboard-header">
          <div className="stats-container">
            <div className="stat-card">
              <h3>Total Tenant</h3>
              <p>{stats.total}</p>
            </div>
            <div className="stat-card">
              <h3>Active</h3>
              <p>{stats.active}</p>
            </div>
            <div className="stat-card">
              <h3>Expired</h3>
              <p>{stats.inactive}</p>
            </div>
            <div className="stat-card">
              <h3>Expiring</h3>
              <p>{stats.expiring}</p>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="secondary" onClick={handleManageTenants}>Create Tenant</button>
          <button className="primary" onClick={handleCreateUser}>Manage Tenants</button>
          <button className="incidents-btn" onClick={handleViewAllIncidents}>View All Incidents</button>
          <button className="tracking-btn" onClick={handleTrackingReport}>View All StandBy</button>
        </div>
        
        <div className="tenant-inventory">
          <h2>Tenant Inventory</h2>
          <table>
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Subscription Plan</th>
                <th>Created Date</th>
                <th>Subscription End Date</th>
                <th>Days Left</th>
                <th>Status</th>
                <th>PO Number</th>
                <th>Invoice Number</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((tenant) => {
                const daysLeft = tenant.expiryDate 
                  ? Math.ceil((tenant.expiryDate.toDate() - new Date()) / (1000 * 60 * 60 * 24))
                  : null;
                  
                return (
                  <tr key={tenant.id}>
                    <td>{tenant.name}</td>
                    <td>{tenant.subscriptionPlan || 'N/A'}</td>
                    <td>{tenant.createdAt?.toDate().toLocaleDateString() || 'N/A'}</td>
                    <td>{tenant.expiryDate?.toDate().toLocaleDateString() || 'N/A'}</td>
                    <td>
                      {daysLeft !== null ? (
                        <span className={daysLeft <= 30 ? 'days-warning' : ''}>
                          {daysLeft > 0 ? daysLeft : 'Expired'}
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td>
                      <span className={`status-badge ${tenant.status}`}>
                        {tenant.status}
                      </span>
                    </td>
                    <td>{tenant.poNumber || 'N/A'}</td>
                    <td>{tenant.invoiceNumber || 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
     
      <footer className="footer">
        <div className="footer-border">
          © 2025 O2k. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default AdminPanel;